import React from 'react';
import {
  cn,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Input,
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from '@strivelabs/ui-core';
import { Button } from '@strivelabs/ui-core/v2';
import { ChevronRight } from 'lucide-react';
import type {
  AgentStep,
  AgentStepContent,
  AgentStepContentTable,
  AgentStepContentChangeTimeline,
  AgentStepContentHypotheses,
  AgentStepContentMedia,
  AgentStepContentSummary,
} from '../../types';
import { RichTextRenderer } from '../rich-text';

export interface AgentStepsBaseProps {
  steps: AgentStep[];
  activeStepId?: string;
  onStepChange?: (id: string) => void;
  className?: string;
  /**
   * Optional render override for the step detail content.
   * If provided, this will be used instead of the default content renderer.
   */
  renderStepContentOverride?: (step: AgentStep) => React.ReactNode;
}

export interface AgentStepTriggerProps {
  step: AgentStep;
  index?: number;
  isActive: boolean;
  onClick: () => void;
  className?: string;
  /**
   * Visual variant for the trigger.
   * - "default": full card with status badge
   * - "link": minimal title-only link with arrow icon
   */
  variant?: 'default' | 'link';
}

export interface AgentStepDetailProps {
  step: AgentStep;
  className?: string;
  /**
   * Optional override for rendering the full detail of the step.
   */
  renderContentOverride?: (step: AgentStep) => React.ReactNode;
}

export const AgentStepTrigger: React.FC<AgentStepTriggerProps> = ({
  step,
  isActive,
  onClick,
  className,
  variant = 'default',
}) => {
  const status = step.status ?? 'pending';

  const statusLabelMap: Record<typeof status, string> = {
    pending: 'Pending',
    in_progress: 'In progress',
    completed: 'Completed',
    error: 'Error',
  };

  const statusVariantMap: Record<typeof status, 'outline' | 'default' | 'destructive' | 'secondary'> = {
    pending: 'secondary',
    in_progress: 'outline',
    completed: 'default',
    error: 'destructive',
  };

  if (variant === 'link') {
    const previewContent = Array.isArray(step.content) ? step.content[0] : step.content;

    return (
      <HoverCard openDelay={120} closeDelay={120}>
        <HoverCardTrigger asChild>
          <button
            type="button"
            onClick={onClick}
            className={cn(
              'flex w-full items-center justify-between gap-2 rounded-md px-1 py-0.5 text-left text-sm font-medium text-custom-text-primary hover:underline',
              className,
            )}
          >
            <span className={cn('truncate', isActive && 'text-custom-text-primary')}>
              {step.title}
            </span>
            <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
          </button>
        </HoverCardTrigger>
        <HoverCardContent
          side="top"
          align="start"
          className="w-[420px] max-w-[480px] max-h-[400px] overflow-auto"
        >
          {step.subtitle ? (
            <p className="mb-1 text-xs font-medium text-custom-text-primary">
              {step.subtitle}
            </p>
          ) : null}
          {previewContent ? (
            <div className="mt-1 space-y-1 text-xs">
              {renderStepContent(previewContent)}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              Preview this step’s details.
            </p>
          )}
        </HoverCardContent>
      </HoverCard>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center justify-between gap-3 rounded-md border-[0.5px] border-custom-border px-3 py-1 text-left',
        className,
      )}
    >
      <div className="flex min-w-0 flex-col">
        <div className="flex items-center gap-2">
          <span className={cn('truncate text-sm font-medium', isActive && 'text-custom-text-primary')}>
            {step.title}
          </span>
        </div>
        {step.subtitle ? (
          <span className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
            {step.subtitle}
          </span>
        ) : null}
      </div>
      <Badge
        variant={statusVariantMap[status]}
        className="shrink-0 text-[11px] font-medium"
      >
        {statusLabelMap[status]}
      </Badge>
    </button>
  );
};

export const AgentStepDetail: React.FC<AgentStepDetailProps> = ({
  step,
  className,
  renderContentOverride,
}) => {
  if (renderContentOverride) {
    return (
      <div className={cn('rounded-md border-[0.5px] border-custom-border p-4', className)}>
        {renderContentOverride(step)}
      </div>
    );
  }

  const contents = Array.isArray(step.content) ? step.content : [step.content];

  return (
    <div className={cn('space-y-4 rounded-md border-[0.5px] border-custom-border p-4', className)}>
      {contents.map((content, index) => (
        <div key={index} className="space-y-2">
          {'title' in content && content.title ? (
            <h4 className="text-sm font-semibold text-custom-text-primary">
              {content.title}
            </h4>
          ) : null}
          {'description' in content && content.description ? (
            <p className="text-xs text-muted-foreground">
              {content.description}
            </p>
          ) : null}
          {renderStepContent(content)}
        </div>
      ))}
    </div>
  );
};

export const AgentStepsBase: React.FC<AgentStepsBaseProps> = ({
  steps,
  activeStepId,
  onStepChange,
  className,
  renderStepContentOverride,
}) => {
  const [internalActiveId, setInternalActiveId] = React.useState<string | undefined>(
    activeStepId ?? steps[0]?.id,
  );

  const resolvedActiveId = activeStepId ?? internalActiveId;

  const handleStepClick = (id: string) => {
    if (!activeStepId) {
      setInternalActiveId(id);
    }
    onStepChange?.(id);
  };

  const activeStep = steps.find((step) => step.id === resolvedActiveId) ?? steps[0];

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-col gap-2">
        {steps.map((step) => (
          <AgentStepTrigger
            key={step.id}
            step={step}
            isActive={step.id === activeStep?.id}
            onClick={() => handleStepClick(step.id)}
          />
        ))}
      </div>
      {activeStep ? (
        <AgentStepDetail
          step={activeStep}
          renderContentOverride={renderStepContentOverride}
        />
      ) : null}
    </div>
  );
};

function renderStepContent(content: AgentStepContent): React.ReactNode {
  switch (content.type) {
    case 'table':
      return <TableContentRenderer content={content} />;
    case 'change_timeline':
      return renderChangeTimelineContent(content);
    case 'hypotheses':
      return <HypothesesContentRenderer content={content} />;
    case 'media':
      return renderMediaContent(content);
    case 'summary':
      return renderSummaryContent(content);
    case 'custom':
    default:
      return null;
  }
}

function TableContentRenderer({ content }: { content: AgentStepContentTable }): React.ReactNode {
  const [search, setSearch] = React.useState('');

  const columnDefs = React.useMemo(() => {
    if (!content.rows || content.rows.length === 0) return [];
    return (
      content.columns ??
      Object.keys(content.rows[0]).map((key) => ({
        id: key,
        label: key,
      }))
    );
  }, [content.columns, content.rows]);

  const rows = React.useMemo(() => {
    if (!content.rows || content.rows.length === 0) return [];
    if (!content.enableQuickFilter || !search.trim()) {
      return content.rows;
    }

    const needle = search.toLowerCase();
    return content.rows.filter((row) =>
      columnDefs.some((column) => {
        const value = row[column.id];
        if (value == null) return false;
        return String(value).toLowerCase().includes(needle);
      }),
    );
  }, [columnDefs, content.enableQuickFilter, content.rows, search]);

  if (!content.rows || content.rows.length === 0) {
    return (
      <p className="text-xs italic text-muted-foreground">
        No data available for this step yet.
      </p>
    );
  }

  return (
    <div className="mt-1 w-full overflow-x-auto space-y-2">
      {content.enableQuickFilter ? (
        <div className="flex items-center justify-between gap-3">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={content.quickFilterPlaceholder ?? 'Filter rows...'}
            className="h-8 max-w-xs text-xs focus-visible:ring-0 focus-visible:ring-offset-0"
          />
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm">
              Filter
            </Button>
          </div>
        </div>
      ) : null}
      <div className="rounded-md border-[0.5px] border-custom-border overflow-hidden">
        <Table variant="minimal" enableExport={false}>
          <TableHeader variant="minimal">
            <TableRow variant="minimal">
              {columnDefs.map((column) => (
                <TableHead
                  key={column.id}
                  variant="minimal"
                  className="px-2 py-[10px] text-sm leading-none font-medium text-custom-text-secondary align-middle"
                >
                  {column.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow key={rowIndex} variant="minimal">
                {columnDefs.map((column) => {
                  const value = row[column.id];

                  if (column.id === 'result' && typeof value === 'string') {
                    const [statusPart, ...rest] = value.split('–');
                    const statusLabel = statusPart.trim();
                    const detail = rest.join('–').trim();

                    const isConfirmed = /confirmed/i.test(statusLabel);
                    const isRuledOut = /ruled out/i.test(statusLabel);

                    const badgeVariant: 'outline' | 'default' | 'destructive' | 'secondary' =
                      isConfirmed ? 'secondary' : isRuledOut ? 'destructive' : 'outline';

                    return (
                      <TableCell
                        key={column.id}
                        variant="minimal"
                        className="whitespace-nowrap px-2 py-[10px] align-top text-sm leading-none text-custom-text-primary"
                      >
                        <div className="flex flex-col gap-1">
                          <Badge variant={badgeVariant} className="w-fit text-[11px]">
                            {statusLabel}
                          </Badge>
                          {detail ? (
                            <span className="max-w-xs text-[11px] text-muted-foreground">
                              {detail}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                    );
                  }

                  const formatted =
                    typeof value === 'number'
                      ? value.toLocaleString()
                      : value ?? '';

                  return (
                    <TableCell
                      key={column.id}
                      variant="minimal"
                      className="whitespace-nowrap px-2 py-[10px] text-sm leading-none text-custom-text-primary"
                    >
                      {formatted}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function renderChangeTimelineContent(content: AgentStepContentChangeTimeline): React.ReactNode {
  if (!content.events.length) {
    return (
      <p className="text-xs italic text-muted-foreground">
        No recent changes recorded for this step.
      </p>
    );
  }

  return (
    <ol className="space-y-3">
      {content.events.map((event, index) => {
        const isLatest = index === 0;
        const isLast = index === content.events.length - 1;

        return (
          <li key={event.id} className="flex gap-3">
            <div className="mt-1 flex flex-col items-center">
              <div className="flex h-5 w-5 items-center justify-center rounded-full border border-custom-border bg-custom-bg-subtle">
                <div
                  className={cn(
                    'h-2.5 w-2.5 rounded-full',
                    isLatest ? 'bg-emerald-500' : 'bg-amber-500',
                  )}
                />
              </div>
              {!isLast ? (
                <div className="mt-1 h-full w-px flex-1 bg-custom-border" />
              ) : null}
            </div>
            <div className="space-y-0.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-custom-text-primary">
                  {event.title}
                </span>
                {event.deltaSummary ? (
                  <Badge variant="outline" className="text-[11px]">
                    {event.deltaSummary}
                  </Badge>
                ) : null}
                {event.timestamp ? (
                  <span className="text-[11px] text-muted-foreground">
                    {event.timestamp}
                  </span>
                ) : null}
              </div>
              {event.description ? (
                <p className="text-xs text-muted-foreground">
                  • {event.description}
                </p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function HypothesesContentRenderer({ content }: { content: AgentStepContentHypotheses }): React.ReactNode {
  if (!content.hypotheses.length) {
    return (
      <p className="text-xs italic text-muted-foreground">
        No hypotheses were generated for this step.
      </p>
    );
  }

  const sorted = [...content.hypotheses].sort((a, b) => b.score - a.score);
  const variant = content.viewVariant ?? 'list';

  const topCount = content.topCount ?? 3;
  const shortlistedIds = content.shortlistedIds && content.shortlistedIds.length > 0
    ? new Set(content.shortlistedIds)
    : new Set(sorted.slice(0, topCount).map((hypothesis) => hypothesis.id));

  const shortlistedHypotheses = sorted.filter((hypothesis) => shortlistedIds.has(hypothesis.id));

  const list = renderHypothesesList(sorted, shortlistedIds);

  if (variant === 'listWithSummary') {
    return (
      <div className="space-y-3">
        {list}
        {renderHypothesesSummary(content, shortlistedHypotheses)}
      </div>
    );
  }

  if (variant === 'matrix') {
    const high: AgentStepContentHypotheses['hypotheses'] = [];
    const medium: AgentStepContentHypotheses['hypotheses'] = [];
    const low: AgentStepContentHypotheses['hypotheses'] = [];

    sorted.forEach((hypothesis) => {
      if (hypothesis.score >= 0.8) {
        high.push(hypothesis);
      } else if (hypothesis.score >= 0.6) {
        medium.push(hypothesis);
      } else {
        low.push(hypothesis);
      }
    });

    const shortlistCards = shortlistedHypotheses.length ? (
      <div className="grid gap-3 sm:grid-cols-3">
        {shortlistedHypotheses.map((hypothesis) => (
          <div
            key={hypothesis.id}
            className="flex flex-col gap-1 rounded-md border border-custom-border bg-custom-bg-subtle px-3 py-2"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-custom-text-primary line-clamp-2">
                {hypothesis.label}
              </span>
              <Badge variant="outline" className="text-[11px]">
                {hypothesis.score.toFixed(2)}
              </Badge>
            </div>
            <div className="flex flex-wrap items-center gap-1">
              {hypothesis.category ? (
                <Badge variant="secondary" className="text-[11px]">
                  {hypothesis.category}
                </Badge>
              ) : null}
              <Badge variant="secondary" className="text-[11px]">
                Shortlisted
              </Badge>
            </div>
            {hypothesis.reasoning ? (
              <p className="text-[11px] text-muted-foreground line-clamp-3">
                {hypothesis.reasoning}
              </p>
            ) : null}
          </div>
        ))}
      </div>
    ) : null;

    const renderBucket = (
      label: string,
      items: AgentStepContentHypotheses['hypotheses'],
    ) => (
      <div className="space-y-1 rounded-md border border-dashed border-custom-border bg-custom-bg-subtle/40 p-3">
        <div className="text-[11px] font-medium text-custom-text-secondary">
          {label}
        </div>
        {items.length ? (
          <ul className="space-y-1 text-xs text-custom-text-primary">
            {items.map((hypothesis) => (
              <li
                key={hypothesis.id}
                className="flex items-center justify-between gap-2"
              >
                <span className="truncate">
                  {hypothesis.label}
                </span>
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  {hypothesis.score.toFixed(2)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[11px] italic text-muted-foreground">
            No hypotheses in this band.
          </p>
        )}
      </div>
    );

    return (
      <div className="space-y-4">
        {shortlistCards}
        <div className="space-y-2">
          <div className="text-xs font-medium text-custom-text-primary">
            How the agent positions these hypotheses
          </div>
          <p className="text-[11px] text-muted-foreground">
            Hypotheses are grouped by how strongly they relate to this report based on their score.
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            {renderBucket('Higher relevance (score ≥ 0.80)', high)}
            {renderBucket('Medium relevance (0.60 – 0.79)', medium)}
            {renderBucket('Lower priority (score &lt; 0.60)', low)}
          </div>
        </div>
        {renderHypothesesSummary(content, shortlistedHypotheses)}
      </div>
    );
  }

  if (variant === 'chartWithSummary') {
    const maxScore = Math.max(...sorted.map((hypothesis) => hypothesis.score || 0), 1);

    return (
      <div className="space-y-3">
        <div className="rounded-md border border-custom-border bg-custom-bg-subtle p-3">
          <div className="mb-2 text-xs font-medium text-custom-text-secondary">
            Hypothesis score distribution
          </div>
          <div className="space-y-1">
            {sorted.map((hypothesis) => {
              const widthPercent = Math.max(6, (hypothesis.score / maxScore) * 100);
              const isShortlisted = shortlistedIds.has(hypothesis.id);

              return (
                <div
                  key={hypothesis.id}
                  className="flex items-center gap-2"
                >
                  <span className="w-6 text-[11px] text-muted-foreground">
                    {hypothesis.score.toFixed(2)}
                  </span>
                  <div className="flex-1">
                    <div
                      className={cn(
                        'h-1.5 rounded-full bg-custom-border',
                        isShortlisted && 'bg-custom-text-primary/60',
                      )}
                      style={{ width: `${widthPercent}%` }}
                    />
                  </div>
                  <span className="truncate text-[11px] text-custom-text-primary">
                    {hypothesis.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="space-y-2">
          {renderHypothesesList(shortlistedHypotheses, shortlistedIds)}
        </div>
        {renderHypothesesSummary(content, shortlistedHypotheses)}
      </div>
    );
  }

  return list;
}

function renderHypothesesList(
  hypotheses: AgentStepContentHypotheses['hypotheses'],
  shortlistedIds: Set<string>,
): React.ReactNode {
  return (
    <div className="space-y-2 max-h-72 overflow-auto pr-1">
      {hypotheses.map((hypothesis, index) => {
        const isShortlisted = shortlistedIds.has(hypothesis.id);

        return (
          <div
            key={hypothesis.id}
            className={cn(
              'flex gap-3 rounded-md border border-custom-border bg-custom-bg-subtle px-3 py-2',
              isShortlisted && 'border-custom-text-primary/40 bg-custom-bg-primary/40',
            )}
          >
            <div className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-custom-card-bg text-xs font-semibold text-custom-text-secondary">
              {index + 1}
            </div>
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-custom-text-primary">
                  {hypothesis.label}
                </span>
                <Badge variant="outline" className="text-[11px]">
                  Score: {hypothesis.score.toFixed(2)}
                </Badge>
                {hypothesis.category ? (
                  <Badge variant="secondary" className="text-[11px]">
                    {hypothesis.category}
                  </Badge>
                ) : null}
                {isShortlisted ? (
                  <Badge variant="secondary" className="text-[11px]">
                    Top pick
                  </Badge>
                ) : null}
              </div>
              {hypothesis.reasoning ? (
                <p className="text-xs text-muted-foreground">
                  {hypothesis.reasoning}
                </p>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function renderHypothesesSummary(
  content: AgentStepContentHypotheses,
  shortlistedHypotheses: AgentStepContentHypotheses['hypotheses'],
): React.ReactNode {
  const hasSummaryPoints = content.summaryPoints && content.summaryPoints.length > 0;

  if (!hasSummaryPoints && shortlistedHypotheses.length === 0) {
    return null;
  }

  const title = content.summaryTitle ?? 'Why these hypotheses are shortlisted';

  return (
    <div className="rounded-md border border-dashed border-custom-border bg-custom-bg-subtle px-3 py-2">
      <div className="mb-1 text-xs font-semibold text-custom-text-primary">
        {title}
      </div>
      {hasSummaryPoints ? (
        <ul className="list-disc space-y-1 pl-4 text-xs text-muted-foreground">
          {content.summaryPoints!.map((point, index) => (
            <li key={index}>
              {point}
            </li>
          ))}
        </ul>
      ) : null}
      {!hasSummaryPoints && shortlistedHypotheses.length > 0 ? (
        <p className="mt-1 text-xs text-muted-foreground">
          The agent selected the top-scoring hypotheses as the most relevant to the current report.
        </p>
      ) : null}
    </div>
  );
}

function renderMediaContent(content: AgentStepContentMedia): React.ReactNode {
  if (!content.items.length) {
    return (
      <p className="text-xs italic text-muted-foreground">
        No media assets are associated with this step yet.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {content.items.map((item) => {
        const inner = (
          <div className="flex flex-col gap-2 rounded-md border border-custom-border bg-custom-bg-subtle p-2">
            <div className="aspect-video w-full overflow-hidden rounded-sm bg-muted">
              <img
                src={item.thumbnailUrl}
                alt={item.label ?? ''}
                className="h-full w-full object-cover"
              />
            </div>
            {item.label ? (
              <span className="line-clamp-2 text-xs font-medium text-custom-text-primary">
                {item.label}
              </span>
            ) : null}
            {item.meta ? (
              <span className="text-[11px] text-muted-foreground">
                {item.meta}
              </span>
            ) : null}
          </div>
        );

        if (item.href) {
          return (
            <a
              key={item.id}
              href={item.href}
              target="_blank"
              rel="noreferrer"
              className="group block"
            >
              {inner}
            </a>
          );
        }

        return (
          <div key={item.id}>
            {inner}
          </div>
        );
      })}
    </div>
  );
}

function renderSummaryContent(content: AgentStepContentSummary): React.ReactNode {
  if (!content.body) {
    return null;
  }

  return (
    <RichTextRenderer content={content.body} />
  );
}

